import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, Search, Edit, Trash2, RefreshCw, LogOut, Package,
  Truck, CheckCircle2, ArrowRight, Clock, ChevronLeft, ChevronRight, X, AlertCircle, Lock, Eye, EyeOff, Settings, Phone, MessageCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import * as db from "../lib/db";
import { businessInfo } from "../data/mock";

const statusColors = {
  "Booked": "bg-blue-100 text-blue-800",
  "In Transit": "bg-amber-100 text-amber-800",
  "Out for Delivery": "bg-orange-100 text-orange-800",
  "Delivered": "bg-green-100 text-green-800",
};

const pickupStatusColors = {
  "New": "bg-red-100 text-red-800",
  "Contacted": "bg-amber-100 text-amber-800",
  "Picked": "bg-green-100 text-green-800",
};

const formatDate = (isoStr) => {
  if (!isoStr) return "";
  const d = new Date(isoStr);
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });
};

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [shipments, setShipments] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("shipments");
  const [adminEmail, setAdminEmail] = useState("");
  const [counts, setCounts] = useState({});

  // Pickup Requests state
  const [pickupRequests, setPickupRequests] = useState([]);
  const [pickupTotal, setPickupTotal] = useState(0);
  const [pickupPages, setPickupPages] = useState(1);
  const [pickupPage, setPickupPage] = useState(1);
  const [pickupStatusFilter, setPickupStatusFilter] = useState("");
  const [pickupLoading, setPickupLoading] = useState(false);

  // Dialogs
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState(null);

  // Password form
  const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  // Add form
  const [addForm, setAddForm] = useState({
    docket_number: "", shipper_name: "", shipper_mobile: "",
    consignee_name: "", consignee_mobile: "", origin_city: "",
    destination_city: "", weight: "", num_pieces: 1,
    booking_date: new Date().toISOString().split("T")[0], remarks: "",
  });

  // Status update form
  const [statusForm, setStatusForm] = useState({ status: "", remarks: "", location: "" });

  // Edit form
  const [editForm, setEditForm] = useState({});

  const fetchShipments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await db.listShipments({ search, status: statusFilter, page, limit: 15 });
      setShipments(res.shipments);
      setTotal(res.total);
      setPages(res.pages);
      setCounts(await db.statusCounts());
    } catch (err) {
      toast.error("Failed to load shipments");
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, page]);

  const fetchPickupRequests = useCallback(async () => {
    setPickupLoading(true);
    try {
      const res = await db.listPickupRequests({ status: pickupStatusFilter, page: pickupPage, limit: 15 });
      setPickupRequests(res.requests);
      setPickupTotal(res.total);
      setPickupPages(res.pages);
    } catch (err) {
      toast.error("Failed to load pickup requests");
    } finally {
      setPickupLoading(false);
    }
  }, [pickupStatusFilter, pickupPage]);

  useEffect(() => {
    let active = true;
    (async () => {
      const session = await db.getSession();
      if (!active) return;
      if (!session) { navigate("/admin/login"); return; }
      setAdminEmail(session.user.email);
      fetchShipments();
      fetchPickupRequests();
    })();
    return () => { active = false; };
  }, [fetchShipments, fetchPickupRequests, navigate]);

  const handleLogout = async () => {
    await db.signOut();
    navigate("/admin/login");
  };

  // Change Password
  const handleChangePassword = async () => {
    if (!passwordForm.current_password || !passwordForm.new_password) {
      toast.error("Please fill all fields."); return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error("New password must be at least 6 characters."); return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("New password and confirm password do not match."); return;
    }
    try {
      await db.changePassword(passwordForm.current_password, passwordForm.new_password);
      toast.success("Password changed successfully!");
      setShowPasswordDialog(false);
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) {
      toast.error(err.message || "Failed to change password");
    }
  };

  // Update Pickup Status
  const handleUpdatePickupStatus = async (requestId, newStatus) => {
    try {
      await db.updatePickupStatus(requestId, newStatus);
      toast.success(`Status updated to ${newStatus}`);
      fetchPickupRequests();
    } catch (err) {
      toast.error("Failed to update pickup status");
    }
  };

  // Delete Pickup Request
  const handleDeletePickup = async (requestId) => {
    try {
      await db.deletePickupRequest(requestId);
      toast.success("Pickup request deleted");
      fetchPickupRequests();
    } catch (err) {
      toast.error("Failed to delete pickup request");
    }
  };

  // Add Shipment
  const handleAddShipment = async () => {
    if (!addForm.docket_number || !addForm.shipper_name || !addForm.consignee_name || !addForm.origin_city || !addForm.destination_city) {
      toast.error("Please fill all required fields."); return;
    }
    try {
      await db.createShipment(addForm);
      toast.success("Shipment added successfully!");
      setShowAddDialog(false);
      setAddForm({ docket_number: "", shipper_name: "", shipper_mobile: "", consignee_name: "", consignee_mobile: "", origin_city: "", destination_city: "", weight: "", num_pieces: 1, booking_date: new Date().toISOString().split("T")[0], remarks: "" });
      fetchShipments();
    } catch (err) {
      toast.error(err.message || "Failed to add shipment");
    }
  };

  // Update Status
  const handleUpdateStatus = async () => {
    if (!statusForm.status) { toast.error("Please select a status."); return; }
    try {
      await db.updateShipmentStatus(selectedShipment.id, statusForm);
      toast.success("Status updated successfully!");
      setShowStatusDialog(false);
      fetchShipments();
    } catch (err) {
      toast.error(err.message || "Failed to update status");
    }
  };

  // Edit Shipment
  const handleEditShipment = async () => {
    try {
      await db.updateShipment(selectedShipment.id, editForm);
      toast.success("Shipment updated successfully!");
      setShowEditDialog(false);
      fetchShipments();
    } catch (err) {
      toast.error(err.message || "Failed to update shipment");
    }
  };

  // Delete Shipment
  const handleDeleteShipment = async () => {
    try {
      await db.deleteShipment(selectedShipment.id);
      toast.success("Shipment deleted successfully!");
      setShowDeleteDialog(false);
      fetchShipments();
    } catch (err) {
      toast.error("Failed to delete shipment");
    }
  };

  const openStatusDialog = (shipment) => {
    setSelectedShipment(shipment);
    setStatusForm({ status: "", remarks: "", location: "" });
    setShowStatusDialog(true);
  };

  const openEditDialog = (shipment) => {
    setSelectedShipment(shipment);
    setEditForm({
      shipper_name: shipment.shipper_name, shipper_mobile: shipment.shipper_mobile,
      consignee_name: shipment.consignee_name, consignee_mobile: shipment.consignee_mobile,
      origin_city: shipment.origin_city, destination_city: shipment.destination_city,
      weight: shipment.weight, num_pieces: shipment.num_pieces,
      booking_date: shipment.booking_date, remarks: shipment.remarks || "",
    });
    setShowEditDialog(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Admin Header */}
      <div className="bg-brand-dark text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={businessInfo.logoUrl} alt="Logo" className="w-10 h-10 rounded-full" />
            <div>
              <h1 className="text-lg font-bold" style={{ fontFamily: "'Lora', serif" }}>Om Sai Express</h1>
              <p className="text-xs text-white/60">Admin Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-white/70 hidden sm:block" data-testid="admin-welcome">Welcome, {adminEmail || "Admin"}</span>
            <Button onClick={() => { setPasswordForm({ current_password: "", new_password: "", confirm_password: "" }); setShowPasswordDialog(true); }} variant="ghost" size="sm" className="text-white hover:bg-white/10">
              <Lock className="w-4 h-4" /> <span className="hidden sm:inline">Change Password</span>
            </Button>
            <Button onClick={handleLogout} variant="ghost" size="sm" className="text-white hover:bg-white/10">
              <LogOut className="w-4 h-4" /> Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6 bg-gray-100">
            <TabsTrigger value="shipments" className="data-[state=active]:bg-brand data-[state=active]:text-white">
              <Package className="w-4 h-4 mr-1.5" /> Shipments
            </TabsTrigger>
            <TabsTrigger value="pickups" className="data-[state=active]:bg-brand data-[state=active]:text-white relative">
              <Truck className="w-4 h-4 mr-1.5" /> Pickup Requests
              {pickupRequests.filter(r => r.status === "New").length > 0 && (
                <span className="ml-2 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {pickupRequests.filter(r => r.status === "New").length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="shipments">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {["Booked", "In Transit", "Out for Delivery", "Delivered"].map((s) => (
            <Card key={s} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setStatusFilter(statusFilter === s ? "" : s); setPage(1); }}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${statusColors[s]}`}>
                  {s === "Booked" && <Package className="w-5 h-5" />}
                  {s === "In Transit" && <Truck className="w-5 h-5" />}
                  {s === "Out for Delivery" && <ArrowRight className="w-5 h-5" />}
                  {s === "Delivered" && <CheckCircle2 className="w-5 h-5" />}
                </div>
                <div>
                  <p className="text-xs text-gray-500">{s}</p>
                  <p className="text-lg font-bold text-gray-900" data-testid={`stat-count-${s.toLowerCase().replace(/ /g, "-")}`}>{counts[s] || 0}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Controls */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search by docket number..."
              className="pl-10 h-10 border-gray-300"
            />
          </div>
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="w-full sm:w-48 h-10">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="Booked">Booked</SelectItem>
              <SelectItem value="In Transit">In Transit</SelectItem>
              <SelectItem value="Out for Delivery">Out for Delivery</SelectItem>
              <SelectItem value="Delivered">Delivered</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => fetchShipments()} variant="outline" size="icon" className="h-10 w-10 shrink-0">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={() => setShowAddDialog(true)} data-testid="add-shipment-btn" className="bg-brand hover:bg-brand-dark text-white font-semibold h-10 shrink-0">
            <Plus className="w-4 h-4" /> Add Shipment
          </Button>
        </div>

        {/* Shipments Table */}
        <Card className="border shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-100 border-b">
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Docket No.</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700 hidden md:table-cell">Shipper</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700 hidden md:table-cell">Consignee</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Route</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Status</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700 hidden lg:table-cell">Date</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="text-center py-12 text-gray-400">Loading...</td></tr>
                ) : shipments.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-12 text-gray-400">No shipments found</td></tr>
                ) : (
                  shipments.map((s) => (
                    <tr key={s.id} className="border-b hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 font-semibold text-gray-900">{s.docket_number}</td>
                      <td className="px-4 py-3 text-gray-600 hidden md:table-cell">
                        <div>{s.shipper_name}</div>
                        <div className="text-xs text-gray-400">{s.shipper_mobile}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-600 hidden md:table-cell">
                        <div>{s.consignee_name}</div>
                        <div className="text-xs text-gray-400">{s.consignee_mobile}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        <span className="text-xs">{s.origin_city} → {s.destination_city}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${statusColors[s.current_status] || "bg-gray-100 text-gray-700"}`}>
                          {s.current_status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs hidden lg:table-cell">{s.booking_date}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button onClick={() => openStatusDialog(s)} variant="ghost" size="icon" className="h-8 w-8 text-brand hover:bg-brand-lightest" title="Update Status">
                            <RefreshCw className="w-3.5 h-3.5" />
                          </Button>
                          <Button onClick={() => openEditDialog(s)} variant="ghost" size="icon" className="h-8 w-8 text-gray-500 hover:bg-gray-100" title="Edit">
                            <Edit className="w-3.5 h-3.5" />
                          </Button>
                          <Button onClick={() => { setSelectedShipment(s); setShowDeleteDialog(true); }} variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:bg-red-50" title="Delete">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t">
            <p className="text-xs text-gray-500">Showing {shipments.length} of {total} shipments</p>
            <div className="flex items-center gap-2">
              <Button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} variant="outline" size="icon" className="h-8 w-8">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-gray-600">Page {page} of {pages}</span>
              <Button onClick={() => setPage(Math.min(pages, page + 1))} disabled={page >= pages} variant="outline" size="icon" className="h-8 w-8">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>
          </TabsContent>

          {/* Pickup Requests Tab */}
          <TabsContent value="pickups">
            {/* Pickup Controls */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <Select value={pickupStatusFilter} onValueChange={(v) => { setPickupStatusFilter(v === "all" ? "" : v); setPickupPage(1); }}>
                <SelectTrigger className="w-full sm:w-48 h-10">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="New">New</SelectItem>
                  <SelectItem value="Contacted">Contacted</SelectItem>
                  <SelectItem value="Picked">Picked</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={() => fetchPickupRequests()} variant="outline" size="icon" className="h-10 w-10 shrink-0">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>

            {/* Pickup Requests Table */}
            <Card className="border shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100 border-b">
                      <th className="text-left px-4 py-3 font-semibold text-gray-700">Customer</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-700">Mobile</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-700 hidden md:table-cell">Pickup Address</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-700">City</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-700 hidden lg:table-cell">Pickup Date</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-700 hidden lg:table-cell">Parcel</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-700">Status</th>
                      <th className="text-right px-4 py-3 font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pickupLoading ? (
                      <tr><td colSpan={8} className="text-center py-12 text-gray-400">Loading...</td></tr>
                    ) : pickupRequests.length === 0 ? (
                      <tr><td colSpan={8} className="text-center py-12 text-gray-400">No pickup requests found</td></tr>
                    ) : (
                      pickupRequests.map((r) => (
                        <tr key={r.id} className="border-b hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 font-medium text-gray-900">{r.customer_name}</td>
                          <td className="px-4 py-3 text-gray-600">{r.mobile}</td>
                          <td className="px-4 py-3 text-gray-600 hidden md:table-cell max-w-[200px] truncate">{r.pickup_address}</td>
                          <td className="px-4 py-3 text-gray-600">{r.city}</td>
                          <td className="px-4 py-3 text-gray-500 text-xs hidden lg:table-cell">{r.pickup_date}</td>
                          <td className="px-4 py-3 text-gray-500 text-xs hidden lg:table-cell">
                            {r.parcel_type && <span>{r.parcel_type}</span>}
                            {r.approx_weight && <span className="block text-gray-400">{r.approx_weight}</span>}
                          </td>
                          <td className="px-4 py-3">
                            <Select value={r.status} onValueChange={(v) => handleUpdatePickupStatus(r.id, v)}>
                              <SelectTrigger className={`h-8 text-xs font-semibold border-0 w-28 ${pickupStatusColors[r.status] || ""}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="New">New</SelectItem>
                                <SelectItem value="Contacted">Contacted</SelectItem>
                                <SelectItem value="Picked">Picked</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button asChild variant="ghost" size="icon" className="h-8 w-8 text-[#25D366] hover:bg-green-50" title="WhatsApp Customer">
                                <a href={`https://wa.me/91${r.mobile.replace(/\D/g, "").slice(-10)}`} target="_blank" rel="noopener noreferrer">
                                  <MessageCircle className="w-3.5 h-3.5" />
                                </a>
                              </Button>
                              <Button asChild variant="ghost" size="icon" className="h-8 w-8 text-brand hover:bg-brand-lightest" title="Call Customer">
                                <a href={`tel:${r.mobile}`}>
                                  <Phone className="w-3.5 h-3.5" />
                                </a>
                              </Button>
                              <Button onClick={() => handleDeletePickup(r.id)} data-testid={`delete-pickup-${r.id}`} variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:bg-red-50" title="Delete">
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t">
                <p className="text-xs text-gray-500">Showing {pickupRequests.length} of {pickupTotal} requests</p>
                <div className="flex items-center gap-2">
                  <Button onClick={() => setPickupPage(Math.max(1, pickupPage - 1))} disabled={pickupPage <= 1} variant="outline" size="icon" className="h-8 w-8">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-gray-600">Page {pickupPage} of {pickupPages}</span>
                  <Button onClick={() => setPickupPage(Math.min(pickupPages, pickupPage + 1))} disabled={pickupPage >= pickupPages} variant="outline" size="icon" className="h-8 w-8">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Shipment Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Lora', serif" }}>Add New Shipment</DialogTitle>
            <DialogDescription>Enter the existing docket number and shipment details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-sm font-medium">Docket Number *</Label>
              <Input value={addForm.docket_number} onChange={(e) => setAddForm({...addForm, docket_number: e.target.value})} placeholder="Enter existing docket number" className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">Shipper Name *</Label>
                <Input value={addForm.shipper_name} onChange={(e) => setAddForm({...addForm, shipper_name: e.target.value})} placeholder="Shipper name" className="mt-1" />
              </div>
              <div>
                <Label className="text-sm font-medium">Shipper Mobile</Label>
                <Input value={addForm.shipper_mobile} onChange={(e) => setAddForm({...addForm, shipper_mobile: e.target.value})} placeholder="Mobile number" className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">Consignee Name *</Label>
                <Input value={addForm.consignee_name} onChange={(e) => setAddForm({...addForm, consignee_name: e.target.value})} placeholder="Consignee name" className="mt-1" />
              </div>
              <div>
                <Label className="text-sm font-medium">Consignee Mobile</Label>
                <Input value={addForm.consignee_mobile} onChange={(e) => setAddForm({...addForm, consignee_mobile: e.target.value})} placeholder="Mobile number" className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">Origin City *</Label>
                <Input value={addForm.origin_city} onChange={(e) => setAddForm({...addForm, origin_city: e.target.value})} placeholder="Origin city" className="mt-1" />
              </div>
              <div>
                <Label className="text-sm font-medium">Destination City *</Label>
                <Input value={addForm.destination_city} onChange={(e) => setAddForm({...addForm, destination_city: e.target.value})} placeholder="Destination city" className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-sm font-medium">Weight</Label>
                <Input value={addForm.weight} onChange={(e) => setAddForm({...addForm, weight: e.target.value})} placeholder="e.g. 2.5 kg" className="mt-1" />
              </div>
              <div>
                <Label className="text-sm font-medium">Pieces</Label>
                <Input type="number" min={1} value={addForm.num_pieces} onChange={(e) => setAddForm({...addForm, num_pieces: parseInt(e.target.value) || 1})} className="mt-1" />
              </div>
              <div>
                <Label className="text-sm font-medium">Booking Date</Label>
                <Input type="date" value={addForm.booking_date} onChange={(e) => setAddForm({...addForm, booking_date: e.target.value})} className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium">Remarks</Label>
              <Input value={addForm.remarks} onChange={(e) => setAddForm({...addForm, remarks: e.target.value})} placeholder="Optional remarks" className="mt-1" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={() => setShowAddDialog(false)} variant="outline" className="flex-1">Cancel</Button>
              <Button onClick={handleAddShipment} className="flex-1 bg-brand hover:bg-brand-dark text-white">Add Shipment</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Lora', serif" }}>Update Shipment Status</DialogTitle>
            <DialogDescription>Docket: {selectedShipment?.docket_number} — Current: {selectedShipment?.current_status}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-sm font-medium">New Status *</Label>
              <Select value={statusForm.status} onValueChange={(v) => setStatusForm({...statusForm, status: v})}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Booked">Booked</SelectItem>
                  <SelectItem value="In Transit">In Transit</SelectItem>
                  <SelectItem value="Out for Delivery">Out for Delivery</SelectItem>
                  <SelectItem value="Delivered">Delivered</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium">Location</Label>
              <Input value={statusForm.location} onChange={(e) => setStatusForm({...statusForm, location: e.target.value})} placeholder="Current location" className="mt-1" />
            </div>
            <div>
              <Label className="text-sm font-medium">Remarks</Label>
              <Textarea value={statusForm.remarks} onChange={(e) => setStatusForm({...statusForm, remarks: e.target.value})} placeholder="Add remarks..." rows={2} className="mt-1 resize-none" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={() => setShowStatusDialog(false)} variant="outline" className="flex-1">Cancel</Button>
              <Button onClick={handleUpdateStatus} className="flex-1 bg-brand hover:bg-brand-dark text-white">Update Status</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Shipment Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Lora', serif" }}>Edit Shipment</DialogTitle>
            <DialogDescription>Docket: {selectedShipment?.docket_number}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">Shipper Name</Label>
                <Input value={editForm.shipper_name || ""} onChange={(e) => setEditForm({...editForm, shipper_name: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label className="text-sm font-medium">Shipper Mobile</Label>
                <Input value={editForm.shipper_mobile || ""} onChange={(e) => setEditForm({...editForm, shipper_mobile: e.target.value})} className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">Consignee Name</Label>
                <Input value={editForm.consignee_name || ""} onChange={(e) => setEditForm({...editForm, consignee_name: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label className="text-sm font-medium">Consignee Mobile</Label>
                <Input value={editForm.consignee_mobile || ""} onChange={(e) => setEditForm({...editForm, consignee_mobile: e.target.value})} className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">Origin City</Label>
                <Input value={editForm.origin_city || ""} onChange={(e) => setEditForm({...editForm, origin_city: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label className="text-sm font-medium">Destination City</Label>
                <Input value={editForm.destination_city || ""} onChange={(e) => setEditForm({...editForm, destination_city: e.target.value})} className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-sm font-medium">Weight</Label>
                <Input value={editForm.weight || ""} onChange={(e) => setEditForm({...editForm, weight: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label className="text-sm font-medium">Pieces</Label>
                <Input type="number" min={1} value={editForm.num_pieces || 1} onChange={(e) => setEditForm({...editForm, num_pieces: parseInt(e.target.value) || 1})} className="mt-1" />
              </div>
              <div>
                <Label className="text-sm font-medium">Booking Date</Label>
                <Input type="date" value={editForm.booking_date || ""} onChange={(e) => setEditForm({...editForm, booking_date: e.target.value})} className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium">Remarks</Label>
              <Input value={editForm.remarks || ""} onChange={(e) => setEditForm({...editForm, remarks: e.target.value})} className="mt-1" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={() => setShowEditDialog(false)} variant="outline" className="flex-1">Cancel</Button>
              <Button onClick={handleEditShipment} className="flex-1 bg-brand hover:bg-brand-dark text-white">Save Changes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Lora', serif" }}>Delete Shipment</DialogTitle>
            <DialogDescription>Are you sure you want to delete docket {selectedShipment?.docket_number}? This cannot be undone.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button onClick={() => setShowDeleteDialog(false)} variant="outline" className="flex-1">Cancel</Button>
            <Button onClick={handleDeleteShipment} className="flex-1 bg-red-600 hover:bg-red-700 text-white">Delete</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Lora', serif" }}>Change Password</DialogTitle>
            <DialogDescription>Enter your current password and set a new one.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-sm font-medium">Current Password *</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type={showCurrentPw ? "text" : "password"}
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                  placeholder="Enter current password"
                  className="pl-10 pr-10"
                />
                <button type="button" onClick={() => setShowCurrentPw(!showCurrentPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showCurrentPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium">New Password *</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type={showNewPw ? "text" : "password"}
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                  placeholder="Enter new password (min 6 chars)"
                  className="pl-10 pr-10"
                />
                <button type="button" onClick={() => setShowNewPw(!showNewPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium">Confirm New Password *</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="password"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                  placeholder="Confirm new password"
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={() => setShowPasswordDialog(false)} variant="outline" className="flex-1">Cancel</Button>
              <Button onClick={handleChangePassword} className="flex-1 bg-brand hover:bg-brand-dark text-white">Change Password</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
